import React, { useState, useRef, useEffect } from 'react';
import {
  SparklesIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { useDashboardChat } from './useDashboardChat';
import type { WidgetConfig } from '../Widget/Widget';

import { useTranslation } from 'react-i18next';

interface DashboardActionMenuProps {
  componentName: string;
  componentData?: Record<string, unknown>;
  contextType?: 'widget' | 'chart' | 'table' | 'metric';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  className?: string;
  config?: WidgetConfig;
  // dashboardId is provided by context when available, gracefully handles missing context
}

export const DashboardActionMenu: React.FC<DashboardActionMenuProps> = ({
  componentName,
  componentData,
  contextType = 'widget',
  position = 'top-right',
  className: _className = '',
  config,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { requestExplanation, attachToChat } = useDashboardChat({
    componentName,
    componentData,
    contextType,
    config,
  });

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-2 left-2';
      case 'bottom-right':
        return 'bottom-2 right-2';
      case 'bottom-left':
        return 'bottom-2 left-2';
      default:
        return 'top-2 right-2';
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150); // 150ms delay before closing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`group/menu absolute z-[100] hidden group-hover/widget:block ${getPositionClasses()}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main action button */}
      <button className="rounded-full bg-white p-2.5 text-gray-500 shadow-md ring-1 ring-gray-200 transition-colors hover:bg-orange-50 hover:text-orange-600 hover:ring-orange-200 dark:bg-gray-900 dark:text-gray-400 dark:ring-gray-700">
        <SparklesIcon className="h-5 w-5" />
      </button>
      {/* Action menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute top-11 right-0 min-w-[11rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          <button
            onClick={() => {
              requestExplanation();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <InformationCircleIcon className="h-4 w-4" />
            {t('learnMore')}
          </button>

          <button
            onClick={() => {
              attachToChat();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <SparklesIcon className="h-4 w-4" />
            {t('askPleri')}
          </button>
        </div>
      )}
    </div>
  );
};
